/**
 * Web Speech API engine — 0 download, EN+VI (OS voices).
 * Cannot export audio file.
 */

import {
  LoadProgress,
  PresetVoice,
  SynthOptions,
  SynthResult,
  TtsEngine,
} from './types';

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

let cachedVoices: PresetVoice[] | null = null;

function waitVoices(): Promise<any[]> {
  const win = getWin();
  const synth = win.speechSynthesis;
  if (!synth) return Promise.resolve([]);

  const existing = synth.getVoices();
  if (existing && existing.length) return Promise.resolve(existing);

  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve(synth.getVoices() || []);
    };
    if (typeof synth.onvoiceschanged !== 'undefined') {
      synth.onvoiceschanged = finish;
    }
    setTimeout(finish, 800);
  });
}

function mapVoices(raw: any[]): PresetVoice[] {
  return raw.map((v, i) => {
    const lang = String(v.lang || '').toLowerCase();
    let code = 'en';
    if (lang.indexOf('vi') === 0) code = 'vi';
    else if (lang.indexOf('en') === 0) code = 'en';
    else code = lang.split('-')[0] || 'other';
    return {
      id: 'ws:' + (v.voiceURI || v.name || i),
      label: (v.name || 'Voice') + ' (' + (v.lang || '?') + ')',
      lang: code,
      gender: undefined,
    };
  });
}

export const webSpeechEngine: TtsEngine = {
  id: 'webspeech',
  supportsCloning: false,
  supportsNativeSpeed: true,
  supportsExaggeration: false,
  canExportFile: false,

  ensureLoaded: async (_modelId: string, onProgress?: (info: LoadProgress) => void) => {
    onProgress && onProgress({ status: 'Đang tải danh sách giọng trình duyệt…', progress: 0.3 });
    const raw = await waitVoices();
    cachedVoices = mapVoices(raw);
    onProgress && onProgress({ status: 'Sẵn sàng.', progress: 1 });
  },

  isReady: () => !!cachedVoices,

  listVoices: async () => {
    if (!cachedVoices) {
      const raw = await waitVoices();
      cachedVoices = mapVoices(raw);
    }
    // Prefer EN + VI first
    const preferred = cachedVoices.filter((v) => v.lang === 'en' || v.lang === 'vi');
    const rest = cachedVoices.filter((v) => v.lang !== 'en' && v.lang !== 'vi');
    return preferred.concat(rest);
  },

  synthesize: async (_modelId: string, opts: SynthOptions): Promise<SynthResult> => {
    const win = getWin();
    const synth = win.speechSynthesis;
    if (!synth) throw new Error('Trình duyệt không hỗ trợ Web Speech API.');

    const text = (opts.text || '').trim();
    if (!text) throw new Error('Hãy nhập văn bản.');

    if (opts.signal && opts.signal.aborted) throw new Error('Đã hủy.');

    const raw = await waitVoices();
    let voice: any = null;
    if (opts.voiceId) {
      const uri = opts.voiceId.replace(/^ws:/, '');
      voice = raw.find((v: any) => (v.voiceURI || v.name) === uri) || null;
    }
    if (!voice && opts.lang) {
      const want = opts.lang.toLowerCase();
      voice =
        raw.find((v: any) => String(v.lang || '').toLowerCase().indexOf(want) === 0) || null;
    }

    // Cancel previous
    try {
      synth.cancel();
    } catch {
      /* ignore */
    }

    return new Promise((resolve, reject) => {
      const u = new win.SpeechSynthesisUtterance(text);
      if (voice) u.voice = voice;
      // rate 0.1–10, we map speed 0.5–2.0
      const speed = typeof opts.speed === 'number' ? opts.speed : 1;
      u.rate = Math.max(0.5, Math.min(2, speed));
      // pitch 0–2, map semitones ≈ ±12 → 0.5–1.5 around 1
      const semi = typeof opts.pitchSemitones === 'number' ? opts.pitchSemitones : 0;
      u.pitch = Math.max(0.5, Math.min(2, 1 + semi / 24));
      if (opts.lang) u.lang = opts.lang === 'vi' ? 'vi-VN' : opts.lang === 'en' ? 'en-US' : opts.lang;

      u.onend = () => {
        // No PCM available from Web Speech — return empty; UI plays via speak only
        resolve({
          pcm: new Float32Array(0),
          sampleRate: 24000,
          nativeSpeedApplied: true,
        });
      };
      u.onerror = (ev: any) => {
        if (opts.signal && opts.signal.aborted) {
          reject(new Error('Đã hủy.'));
          return;
        }
        reject(new Error((ev && ev.error) || 'Phát giọng thất bại.'));
      };

      // Poll abort
      const poll = setInterval(() => {
        if (opts.signal && opts.signal.aborted) {
          clearInterval(poll);
          try {
            synth.cancel();
          } catch {
            /* ignore */
          }
          reject(new Error('Đã hủy.'));
        }
      }, 200);

      u.onend = () => {
        clearInterval(poll);
        resolve({
          pcm: new Float32Array(0),
          sampleRate: 24000,
          nativeSpeedApplied: true,
        });
      };

      opts.onProgress && opts.onProgress({ status: 'Đang phát…' });
      synth.speak(u);
    });
  },

  cancel: () => {
    const win = getWin();
    try {
      if (win.speechSynthesis) win.speechSynthesis.cancel();
    } catch {
      /* ignore */
    }
  },
};

/**
 * Speak via Web Speech without going through synthesize result PCM.
 * Used by UI when engine is webspeech.
 */
export async function speakWebSpeech(opts: SynthOptions): Promise<void> {
  await webSpeechEngine.synthesize('webspeech', opts);
}
